import { useState, useRef, useEffect, useCallback } from 'react';
import { LayoutGrid as Layout, Share2, Globe, Printer, Mail, Sliders, ChevronDown, Search, ArrowLeftRight } from 'lucide-react';
import { useCanvasStore } from '../../stores/canvasStore';
import { useStudioStore } from '../../stores/studioStore';
import { ARTBOARD_PRESETS, PRESET_CATEGORIES } from '../../lib/artboardPresets';
import type { ArtboardPreset } from '../../types';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  social: <Share2 className="w-3.5 h-3.5" />,
  web: <Globe className="w-3.5 h-3.5" />,
  print: <Printer className="w-3.5 h-3.5" />,
  email: <Mail className="w-3.5 h-3.5" />,
  custom: <Sliders className="w-3.5 h-3.5" />,
};

export default function ArtboardPresetSelector() {
  const { canvasWidth, canvasHeight } = useCanvasStore();
  const setCanvasDimensions = useStudioStore((s) => s.setCanvasDimensions);
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('social');
  const [searchQuery, setSearchQuery] = useState('');
  const [customWidth, setCustomWidth] = useState(canvasWidth.toString());
  const [customHeight, setCustomHeight] = useState(canvasHeight.toString());
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCustomWidth(canvasWidth.toString());
    setCustomHeight(canvasHeight.toString());
  }, [canvasWidth, canvasHeight]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handlePresetSelect = useCallback(
    (preset: ArtboardPreset) => {
      setCanvasDimensions(preset.width, preset.height);
      setIsOpen(false);
    },
    [setCanvasDimensions],
  );

  const handleCustomApply = useCallback(() => {
    const w = parseInt(customWidth, 10);
    const h = parseInt(customHeight, 10);
    if (w > 0 && h > 0 && w <= 20000 && h <= 20000) {
      setCanvasDimensions(w, h);
      setIsOpen(false);
    }
  }, [customWidth, customHeight, setCanvasDimensions]);

  const handleSwapDimensions = useCallback(() => {
    setCanvasDimensions(canvasHeight, canvasWidth);
  }, [canvasWidth, canvasHeight, setCanvasDimensions]);

  const filteredPresets = searchQuery
    ? ARTBOARD_PRESETS.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          `${p.width}x${p.height}`.includes(searchQuery),
      )
    : ARTBOARD_PRESETS.filter((p) => p.category === activeCategory);

  const currentPreset = ARTBOARD_PRESETS.find(
    (p) => p.width === canvasWidth && p.height === canvasHeight,
  );

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-2.5 py-1 rounded-md text-xs transition-all ${
          isOpen
            ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
            : 'bg-white/5 text-white/50 border border-transparent hover:bg-white/10 hover:text-white/70'
        }`}
        title="Artboard size"
      >
        <Layout className="w-3.5 h-3.5" />
        <span className="font-mono text-[11px]">
          {canvasWidth} x {canvasHeight}
        </span>
        {currentPreset && (
          <span className="text-white/30 hidden xl:inline text-[10px]">({currentPreset.name})</span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-[400px] bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden">
          <div className="p-3 border-b border-white/5">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search presets..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-lg text-white/80 placeholder-white/30 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          </div>

          {!searchQuery && (
            <div className="flex gap-1 px-3 py-2 border-b border-white/5 overflow-x-auto">
              {PRESET_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs whitespace-nowrap transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/5 border border-transparent'
                  }`}
                >
                  {CATEGORY_ICONS[cat.id]}
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          <div className="max-h-[280px] overflow-y-auto sidebar-scroll">
            {filteredPresets.length === 0 ? (
              <div className="p-4 text-center text-xs text-white/30">
                No presets found
              </div>
            ) : (
              <div className="p-2">
                {filteredPresets.map((preset) => {
                  const isActive = preset.width === canvasWidth && preset.height === canvasHeight;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                        isActive
                          ? 'bg-cyan-500/10 text-cyan-300'
                          : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-medium">{preset.name}</span>
                        {preset.description && (
                          <span className="text-[10px] text-white/30 mt-0.5">{preset.description}</span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono text-white/30 ml-3">
                        {preset.width}x{preset.height}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/5 bg-white/[0.02]">
            <div className="text-[10px] text-white/30 uppercase tracking-wider mb-2 font-medium">Custom Size</div>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[10px] text-white/40 mb-0.5 block">Width</label>
                <input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomApply()}
                  min={1}
                  max={20000}
                  className="w-full px-2 py-1 text-xs font-mono bg-white/5 border border-white/10 rounded text-white/80 focus:outline-none focus:border-cyan-500/50"
                />
              </div>
              <button
                onClick={handleSwapDimensions}
                className="mt-4 p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white/60 transition-colors"
                title="Swap width and height"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
              </button>
              <div className="flex-1">
                <label className="text-[10px] text-white/40 mb-0.5 block">Height</label>
                <input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomApply()}
                  min={1}
                  max={20000}
                  className="w-full px-2 py-1 text-xs font-mono bg-white/5 border border-white/10 rounded text-white/80 focus:outline-none focus:border-cyan-500/50"
                />
              </div>
              <button
                onClick={handleCustomApply}
                className="mt-4 px-3 py-1 text-xs bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 rounded hover:bg-cyan-500/25 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
